import axios, { AxiosError, AxiosInstance } from 'axios';

export interface GalleryVideoContent {
    id: number;
    link_video: string;
    created_at: string;
    updated_at: string;
}

export interface CreateGalleryVideoData {
    link_video: string;  // Changed to required string
}

export interface UpdateGalleryVideoData extends CreateGalleryVideoData {
    id: number;
}

class GalleryVideoClass {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            timeout: 30000,
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const storageType = localStorage.getItem('storageType');
                const storage = storageType === 'local' ? localStorage : sessionStorage;
                const token = storage.getItem('token');
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );
    }

    async createGalleryVideo(data: CreateGalleryVideoData): Promise<GalleryVideoContent | undefined> {
        try {
            const response = await this.axiosInstance.post<{
                message: string;
                data: GalleryVideoContent;
            }>('/admin/gallery/video/', {
                link_video: data.link_video
            });

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to create gallery video');
            return undefined;
        }
    }

    async getGalleryVideo(id: number): Promise<GalleryVideoContent | undefined> {
        try {
            const response = await this.axiosInstance.get<{
                data: GalleryVideoContent;
            }>(`/gallery/video/${id}`);
            
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve gallery video');
            return undefined;
        }
    }

    async updateGalleryVideo(data: UpdateGalleryVideoData): Promise<GalleryVideoContent | undefined> {
        try {
            const response = await this.axiosInstance.put<{
                message: string;
                data: GalleryVideoContent;
            }>(`/admin/gallery/video/${data.id}`, {
                link_video: data.link_video
            });

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to update gallery video');
            return undefined;
        }
    }

    async deleteGalleryVideo(id: number): Promise<void> {
        try {
            await this.axiosInstance.delete(`/admin/gallery/video/${id}`);
        } catch (error) {
            this.handleError(error, 'Failed to delete gallery video');
        }
    }

    async getAllGalleryVideos(): Promise<GalleryVideoContent[]> {
        try {
            const response = await this.axiosInstance.get<{
                data: GalleryVideoContent[];
            }>('/gallery/video');

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve gallery videos');
            return [];
        }
    }

    private handleError(error: unknown, message: string) {
        if (error instanceof AxiosError) {
            console.error(`${message}:`, error.response?.data);
            throw error.response?.data || { error: message };
        }
        console.error(message, error);
        throw { error: message };
    }
}

export const galleryVideoClass = new GalleryVideoClass();